import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { Button, Col, Row, Form, Alert, Spinner } from "react-bootstrap";
import { FiSettings, FiArrowLeft } from "react-icons/fi";
import { getHost, defaultClientId } from "../utils/utils";

const CLIENTS_API = `${getHost()}/api/clients`;
const CLIENT_API = `${getHost()}/api/client/webhook`;
const getToken = () => localStorage.getItem("jwt");

const GeneralSettings = () => {
  const { toggleSidebar } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [client, setClient] = useState({
    name: "",
    avatar: "",
    color: "#00E98E",
    footer: { icon_url: "", text: "" },
    use_group_name: false,
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Fetch all clients on mount
  useEffect(() => {
    setLoading(true);
    fetch(CLIENTS_API, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load clients");
        return res.json();
      })
      .then((data) => {
        const list = data.clients || [];
        setClients(list);
        setSelectedClient((prev) => prev || defaultClientId(list));
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load clients.");
        setLoading(false);
      });
  }, []);

  // Fetch config for selected client
  useEffect(() => {
    if (!selectedClient) return;
    setLoading(true);
    fetch(`${CLIENT_API}?client_id=${selectedClient}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load config");
        return res.json();
      })
      .then((data) => {
        setClient({
          name: data.name || "",
          avatar: data.avatar || "",
          color: "#" + (data.color?.toString(16).padStart(6, "0") || "00E98E"),
          footer: {
            icon_url: data.footer?.icon_url || "",
            text: data.footer?.text || "",
          },
          use_group_name: data.use_group_name || false,
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load config.");
        setLoading(false);
      });
  }, [selectedClient]);

  const handleChange = (field, val) => {
    if (field.startsWith("footer.")) {
      setClient((c) => ({
        ...c,
        footer: { ...c.footer, [field.split(".")[1]]: val },
      }));
    } else {
      setClient((c) => ({ ...c, [field]: val }));
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    const toSend = {
      ...client,
      color:
        typeof client.color === "string"
          ? parseInt(client.color.replace("#", ""), 16)
          : client.color,
      client_id: selectedClient,
    };

    fetch(CLIENT_API, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(toSend),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to update config");
        return res.json();
      })
      .then(() => {
        setSuccess(true);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not update config.");
        setLoading(false);
      });
  };

  return (
    <div className="w-100 dashboard-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="px-4 pb-3 content-body">
        {/* Header */}
        <div className="mb-3">
          <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
            <FiSettings className="text-white" />
          </div>
          <h2 className="text-main fs-20 fw-600 mb-1">
            Manage your Group Settings
          </h2>
          <p className="text-white-65 fs-14 mb-0">
            Enter details for your discord group webhook messages
          </p>
        </div>

        {/* Client selector */}
        <Row className="mb-3">
          <Col md={7}>
            <Form.Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="form-input-field rounded-3 text-white-65 custom-select-white py-2 px-3"
              disabled={loading}
            >
              <option value="">Select a group...</option>
              {clients.map((cl) => (
                <option key={cl._id} value={cl._id}>
                  {cl.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={12}>
            <hr className="border-secondary opacity-25 my-3" />
          </Col>
        </Row>

        {/* Only show the form if a client is selected */}
        {selectedClient && (
          <Form onSubmit={handleUpdate}>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" className="mb-3">
                Updated!
              </Alert>
            )}

            {/* Group Name */}
            <Row className="align-items-center mb-3">
              <Col md={7}>
                <Form.Control
                  type="text"
                  className="form-input-field border rounded py-2 px-3"
                  placeholder="Your Group Name"
                  value={client.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </Col>
              <Col
                md={5}
                className="d-flex align-items-center justify-content-start justify-content-md-end order-first order-md-last mb-2 mb-md-0"
              >
                <div className="text-start text-md-end">
                  <div className="text-main fs-13 fw-500 mb-1">Group Name</div>
                  <div className="text-white-45 fs-11 d-flex align-items-center justify-content-start justify-content-md-end">
                    <FiArrowLeft className="me-1 d-none d-md-inline" />
                    Can't find it? Learn more
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <hr className="border-secondary opacity-25 my-3" />
              </Col>
            </Row>

            {/* Hexa Decimal Color */}
            <Row className="align-items-center mb-3">
              <Col md={7}>
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center form-input-field rounded-3 flex-grow-1">
                    <span className="text-gray ps-3 fw-bold fs-14">#</span>
                    <Form.Control
                      type="text"
                      className="form-control bg-transparent border-0 text-white-65 py-2"
                      value={client.color.replace("#", "").toUpperCase()}
                      onChange={(e) =>
                        handleChange("color", "#" + e.target.value)
                      }
                      maxLength={6}
                    />
                  </div>
                  <div className="ms-2">
                    <Form.Control
                      type="color"
                      value={client.color}
                      onChange={(e) => handleChange("color", e.target.value)}
                      style={{
                        width: 60,
                        height: 40,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                </div>
              </Col>
              <Col
                md={5}
                className="d-flex align-items-center justify-content-start justify-content-md-end order-first order-md-last mb-2 mb-md-0"
              >
                <div className="text-start text-md-end">
                  <div className="text-main fs-13 fw-500 mb-1">
                    Hexa Decimal
                  </div>
                  <div className="text-white-45 fs-11 d-flex align-items-center justify-content-start justify-content-md-end">
                    <FiArrowLeft className="me-1 d-none d-md-inline" />
                    Can't find it? Learn more
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <hr className="border-secondary opacity-25 my-3" />
              </Col>
            </Row>

            {/* Footer Image URL */}
            <Row className="align-items-center mb-3">
              <Col md={7}>
                <Form.Control
                  type="text"
                  className="form-input-field border rounded py-2 px-3"
                  placeholder="Enter Image URL"
                  value={client.footer.icon_url}
                  onChange={(e) =>
                    handleChange("footer.icon_url", e.target.value)
                  }
                />
              </Col>
              <Col
                md={5}
                className="d-flex align-items-center justify-content-start justify-content-md-end order-first order-md-last mb-2 mb-md-0"
              >
                <div className="text-start text-md-end">
                  <div className="text-main fs-13 fw-500 mb-1">
                    Footer Image URL
                  </div>
                  <div className="text-white-45 fs-11 d-flex align-items-center justify-content-start justify-content-md-end">
                    <FiArrowLeft className="me-1 d-none d-md-inline" />
                    Can't find it? Learn more
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <hr className="border-secondary opacity-25 my-3" />
              </Col>
            </Row>

            {/* Footer Text */}
            <Row className="align-items-center mb-3">
              <Col md={7}>
                <Form.Control
                  type="text"
                  className="form-input-field border rounded py-2 px-3"
                  placeholder="Enter footer text"
                  value={client.footer.text}
                  onChange={(e) => handleChange("footer.text", e.target.value)}
                />
              </Col>
              <Col
                md={5}
                className="d-flex align-items-center justify-content-start justify-content-md-end order-first order-md-last mb-2 mb-md-0"
              >
                <div className="text-start text-md-end">
                  <div className="text-main fs-13 fw-500 mb-1">Footer Text</div>
                  <div className="text-white-45 fs-11 d-flex align-items-center justify-content-start justify-content-md-end">
                    <FiArrowLeft className="me-1 d-none d-md-inline" />
                    Can't find it? Learn more
                  </div>
                </div>
              </Col>
            </Row>

            {/* Use group name */}
            <Row className="align-items-center mb-3">
              <Col md={7}>
                <Form.Check
                  type="checkbox"
                  id="use-group-name"
                  label="Use group name as webhook username"
                  checked={client.use_group_name || false}
                  onChange={(e) =>
                    handleChange("use_group_name", e.target.checked)
                  }
                  className="text-white-65"
                />
              </Col>
              <Col
                md={5}
                className="d-flex align-items-center justify-content-start justify-content-md-end order-first order-md-last mb-2 mb-md-0"
              >
                <div className="text-start text-md-end">
                  <div className="text-main fs-13 fw-500 mb-1">
                    Webhook Username
                  </div>
                  <div className="text-white-45 fs-11 d-flex align-items-center justify-content-start justify-content-md-end">
                    <FiArrowLeft className="me-1 d-none d-md-inline" />
                    Use group name instead of site name
                  </div>
                </div>
              </Col>
            </Row>
            <Col md={12}>
              <hr className="border-secondary opacity-25 my-3" />
            </Col>

            {/* Update Button */}
            <Row>
              <Col md={7}>
                <div className="mt-4">
                  <Button
                    type="submit"
                    className="btn-primary rounded-3 py-2 px-4 fw-600 fs-14"
                    disabled={loading}
                    style={{
                      backgroundColor: "#4A4A4A",
                      borderColor: "#4A4A4A",
                      color: "white",
                    }}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Update Information
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        )}

      </div>
    </div>
  );
};

export default GeneralSettings;
